/** Upper bound (ms) before third-party scripts run if `window` `load` is slow. */
export const THIRD_PARTY_LOAD_OR_DELAY_MS = 5000;

/**
 * Runs once on the earlier of: `window` `load`, or `delayMs` after this call.
 * Clears the timer if `load` wins.
 */
export function scheduleAfterLoadOrDelay(fn: () => void, delayMs: number = THIRD_PARTY_LOAD_OR_DELAY_MS): void {
  let ran = false;
  const run = (): void => {
    if (ran) {
      return;
    }
    ran = true;
    fn();
  };
  if (document.readyState === 'complete') {
    run();
    return;
  }
  const t = window.setTimeout(run, delayMs);
  window.addEventListener(
    'load',
    () => {
      window.clearTimeout(t);
      run();
    },
    { once: true }
  );
}

export type IdleOrInteractionOptions = {
  includeScroll?: boolean;
};

/** When `requestIdleCallback` is missing, defer one animation frame (no `setTimeout`). */
function requestIdleCallbackPolyfill(cb: IdleRequestCallback): void {
  requestAnimationFrame(() => {
    cb({ didTimeout: false, timeRemaining: () => 5 } as IdleDeadline);
  });
}

function scheduleIdleCallback(cb: IdleRequestCallback): void {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(cb);
  } else {
    requestIdleCallbackPolyfill(cb);
  }
}

function afterElapsedMs(ms: number, onReady: () => void): void {
  const deadline = performance.now() + ms;
  const tick = (): void => {
    if (performance.now() >= deadline) {
      onReady();
      return;
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/**
 * Runs a callback on the first user interaction OR first idle slice after window load
 * (whichever comes first). Use to avoid competing with critical-path main-thread work.
 */
export function runWhenIdleOrInteraction(fn: () => void, options?: IdleOrInteractionOptions): void {
  let ran = false;
  const run = (): void => {
    if (ran) {
      return;
    }
    ran = true;
    fn();
  };

  const includeScroll = options?.includeScroll !== false;
  const events: Array<keyof WindowEventMap> = includeScroll
    ? ['scroll', 'click', 'keydown', 'touchstart', 'pointerdown']
    : ['click', 'keydown', 'touchstart', 'pointerdown'];
  events.forEach((ev) => {
    window.addEventListener(ev, run, { once: true, passive: true });
  });

  window.addEventListener(
    'load',
    () => {
      scheduleIdleCallback(() => run());
    },
    { once: true }
  );
}

/**
 * Milliseconds after window `load` before Infinity may schedule (keeps LCP clear).
 * Kept short so header `tel:` can update before most dials on mobile.
 */
const INFINITY_GRACE_MS_AFTER_LOAD = 1000;

/**
 * After grace: idle OR first gesture.
 * Gesture-only meant the phone icon often dialled the CMS number before Infinity loaded.
 */
function runWhenIdleOrInteractionAfterGraceForInfinity(fn: () => void): void {
  let ran = false;
  const run = (): void => {
    if (ran) {
      return;
    }
    ran = true;
    fn();
  };

  window.addEventListener(
    'load',
    () => {
      afterElapsedMs(INFINITY_GRACE_MS_AFTER_LOAD, () => {
        const events: Array<keyof WindowEventMap> = ['click', 'keydown', 'touchstart', 'pointerdown'];
        events.forEach((ev) => {
          window.addEventListener(ev, run, { once: true, passive: true });
        });
        scheduleIdleCallback(() => run());
      });
    },
    { once: true }
  );
}

function loadScript(src: string, attributes?: Record<string, string>): void {
  const s = document.createElement('script');
  s.src = src;
  s.async = true;
  if (attributes) {
    Object.entries(attributes).forEach(([k, v]) => s.setAttribute(k, v));
  }
  document.head.appendChild(s);
}

/** Salesforce Marketing Cloud collect.js — was blocking in <head>. */
export function initSalesforceCollectDeferred(): void {
  const id = document.querySelector('meta[name="sf-collect-company-id"]')?.getAttribute('content')?.trim();
  if (!id) {
    return;
  }
  runWhenIdleOrInteraction(() => {
    loadScript(`https://${id}.collect.igodigital.com/collect.js`);
  });
}

/** Infinity call tracking + fbq bridge — was inline in <head>. */
export function initInfinityTrackingDeferred(): void {
  const raw = document.querySelector('meta[name="infinity-tracking-id"]')?.getAttribute('content')?.trim();
  if (!raw) {
    return;
  }
  const companyId = Number(raw);
  if (Number.isNaN(companyId)) {
    return;
  }

  runWhenIdleOrInteractionAfterGraceForInfinity(() => {
    const w = window as Window & {
      _its?: (...args: unknown[]) => void;
      uniqueId?: string;
      fbq?: unknown;
    };

    (function (i: Window, n: Document, f: string, t: string, y?: any) {
      y = (i as any)._its = function () {
        return y.queue.push(arguments);
      };
      y.version = '2.0';
      y.queue = [];
      const z = n.createElement(f) as HTMLScriptElement;
      z.async = true;
      z.src = t;
      const x = n.getElementsByTagName(f)[0];
      x.parentNode!.insertBefore(z, x);
    })(window, document, 'script', `https://script.infinity-tracking.com/infinitytrack.js?i=${companyId}`);

    w.uniqueId =
      w.uniqueId ||
      (`${1e7}${1e3}${4e3}${8e3}${1e11}` as string).replace(/[018]/g, function (e) {
        return (Number(e) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> Number(e) / 4))).toString(16);
      });

    const uuid = w.uniqueId;
    let fbqProxy: unknown;
    let _fbq: unknown;
    const fbqHandler = {
      get(e: object, i: string | symbol) {
        return Reflect.get(e, i);
      },
      apply(e: (...args: unknown[]) => unknown, i: unknown, n: unknown[]) {
        if (n[0] === 'init' && w._its) {
          w._its('setVariable', 'fb_pid', n[1]);
          w._its('setVariable', 'fb_uuid', uuid);
          if (n[2]) {
            (n[2] as { external_id?: string }).external_id = uuid;
          } else {
            n.push({ external_id: uuid });
          }
        }
        return e.apply(i, n);
      }
    };
    if (w.fbq) {
      _fbq = w.fbq;
    }
    Object.defineProperty(w, 'fbq', {
      get() {
        return fbqProxy;
      },
      set(e: unknown) {
        fbqProxy = new Proxy(e as object, fbqHandler);
      },
      configurable: true
    });
    if (_fbq) {
      w.fbq = _fbq;
    }
    // `observeChanges` (Infinity's default) re-walks every text node under
    // `document.body` on each DOM mutation — one `requestIdleCallback` per node,
    // up to 30 times a second. We init after load + idle, so the DOM has already
    // settled and the single initial scan covers the same numbers.
    w._its?.('init', companyId, { observeChanges: false });
    w._its?.('track');
  });
}

const TRUSTPILOT_BOOTSTRAP = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

/** Official Trustpilot widget bootstrap — load only when an official TrustBox is near the viewport. */
export function initTrustpilotBootstrapDeferred(): void {
  const anchors = document.querySelectorAll('.trustpilot-widget, [class*="trustpilot-widget"]');

  if (!anchors.length) {
    return;
  }

  let loaded = false;
  const load = (): void => {
    if (loaded) {
      return;
    }
    loaded = true;
    loadScript(TRUSTPILOT_BOOTSTRAP, { 'data-cookieconsent': 'ignore' });
  };

  if (typeof IntersectionObserver === 'undefined') {
    window.addEventListener('load', load, { once: true });
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        load();
        io.disconnect();
      }
    },
    { rootMargin: '200px 0px', threshold: 0.01 }
  );

  anchors.forEach((el) => io.observe(el));
}
